/* __next_internal_client_entry_do_not_use__ default auto */ import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useLayoutEffect, useMemo, useRef, useCallback, useState } from 'react';
import { Splide, SplideSlide, SplideTrack } from '@splidejs/react-splide';
import '@splidejs/splide/css';
import { tv } from 'tailwind-variants';
import { withStandardComponentWrapper } from 'helpers/HOC';
import ImageWrapper from 'helpers/SitecoreWrappers/ImageWrapper/ImageWrapper';
import LinkWrapper from 'helpers/SitecoreWrappers/LinkWrapper/LinkWrapper';
import PlainTextWrapper from 'helpers/SitecoreWrappers/PlainTextWrapper/PlainTextWrapper';
import useDictionary from 'lib/hooks/useDictionary';
import useIsEditing from 'lib/hooks/useIsEditing';
import { editingHydrationProps } from 'lib/hooks/useEditingHydrationProps';
import { useIsMobile } from 'lib/hooks/useIsMobile';
import { useSetIsDataAvailable } from 'lib/context/IsDataAvailableContext';
import RichTextWrapper from 'helpers/SitecoreWrappers/RichTextWrapper/RichTextWrapper';
import { EditingHelpText } from 'helpers/Editing/EditingHelpText';
import SvgIcon from 'helpers/SvgIcon/SvgIcon';
import ButtonWrapper from 'helpers/SitecoreWrappers/ButtonWrapper/ButtonWrapper';
const joinAddressLines = (...lines)=>lines.filter(Boolean).join(', ');
const mapGraphQLCard = (item)=>{
    const office = item.authoritativeItem?.targetItem;
    if (!office) return null;
    return {
        id: item.id,
        displayName: item.displayName?.jsonValue,
        image: office.image?.jsonValue,
        address: joinAddressLines(office.addressFirstLine?.jsonValue?.value, office.addressSecondLine?.jsonValue?.value, office.addressThirdLine?.jsonValue?.value),
        telephone: office.telephone?.jsonValue,
        openingHours: office.openingHours?.jsonValue,
        ctaHref: office.url?.url
    };
};
const resolveOfficeLocations = (props)=>{
    const propsItems = props?.fields?.data?.item?.location?.targetItems ?? [];
    return propsItems.map(mapGraphQLCard).filter((item)=>item !== null);
};
const OfficeLocations = (props)=>{
    const isEditing = useIsEditing();
    const isMobile = useIsMobile();
    const setIsDataAvailable = useSetIsDataAvailable();
    const splideRef = useRef(null);
    const mobileSplideRef = useRef(null);
    const desktopSplideRef = useRef(null);
    const pairGridRef = useRef(null);
    const addressRefs = useRef(new Map());
    const [currentIndex, setCurrentIndex] = useState(0);
    const [endIndex, setEndIndex] = useState(null);
    const setAddressRef = useCallback((id, el)=>{
        if (el) addressRefs.current.set(id, el);
        else addressRefs.current.delete(id);
    }, []);
    const equalizeAddressHeights = useCallback(()=>{
        const elements = Array.from(addressRefs.current.values());
        elements.forEach((el)=>{
            el.style.minHeight = '';
        });
        const maxHeight = Math.max(...elements.map((el)=>el.offsetHeight), 0);
        if (maxHeight > 0) {
            elements.forEach((el)=>{
                el.style.minHeight = `${maxHeight}px`;
            });
        }
    }, []);
    const validLocations = resolveOfficeLocations(props);
    const { getDictionaryValue } = useDictionary();
    const viewText = getDictionaryValue('View')?.trim();
    const openingHoursLabel = getDictionaryValue('OpeningHours')?.trim();
    const telephoneLabel = getDictionaryValue('TelephoneLabel')?.trim();
    const hasNoLocations = validLocations.length === 0;
    const slideCount = validLocations.length;
    const isSingleCard = slideCount === 1;
    const isTwoCards = slideCount === 2;
    const isThreeCards = slideCount === 3;
    const showDesktopArrows = slideCount >= 4;
    const isPrevDisabled = currentIndex === 0;
    const isNextDisabled = endIndex !== null && currentIndex >= endIndex;
    const layout = isSingleCard ? 'single' : isTwoCards ? 'pair' : 'multi';
    const onDesktopSplideMove = (instance)=>{
        setCurrentIndex(instance.index);
        setEndIndex(instance.Components.Controller.getEnd());
    };
    const mobileSplideOptions = useMemo(()=>({
            arrows: false,
            pagination: slideCount > 1,
            perPage: 1,
            perMove: 1,
            drag: slideCount > 1,
            gap: '8px',
            omitEnd: true,
            rewind: false,
            rewindByDrag: false,
            width: '100%'
        }), [
        slideCount
    ]);
    const desktopMultiSplideOptions = useMemo(()=>({
            arrows: false,
            pagination: slideCount > 3,
            perPage: 3,
            perMove: 1,
            drag: true,
            gap: '8px',
            omitEnd: true,
            rewind: false,
            rewindByDrag: false,
            width: '100%',
            padding: {
                right: '17%'
            },
            breakpoints: {
                1024: {
                    perPage: 2,
                    padding: {
                        right: '20%'
                    }
                }
            }
        }), [
        slideCount
    ]);
    useEffect(()=>{
        const available = !isEditing && !hasNoLocations;
        setIsDataAvailable(available);
    }, [
        hasNoLocations,
        isEditing,
        setIsDataAvailable
    ]);
    useLayoutEffect(()=>{
        if (hasNoLocations || isSingleCard) return;
        equalizeAddressHeights();
        const resizeObserver = new ResizeObserver(equalizeAddressHeights);
        const splideRoot = splideRef.current?.root || mobileSplideRef.current?.root || desktopSplideRef.current?.root;
        if (splideRoot) resizeObserver.observe(splideRoot);
        if (pairGridRef.current) resizeObserver.observe(pairGridRef.current);
        window.addEventListener('resize', equalizeAddressHeights);
        return ()=>{
            resizeObserver.disconnect();
            window.removeEventListener('resize', equalizeAddressHeights);
        };
    }, [
        hasNoLocations,
        validLocations,
        equalizeAddressHeights,
        isSingleCard
    ]);
    const singleSplideOptions = useMemo(()=>({
            arrows: false,
            pagination: false,
            perPage: 1,
            perMove: 1,
            drag: false,
            gap: '0',
            rewind: true,
            width: '100%',
            padding: {
                right: 0,
                left: 0
            }
        }), []);
    useEffect(()=>{
        requestAnimationFrame(()=>{
            [
                splideRef,
                mobileSplideRef,
                desktopSplideRef
            ].forEach((ref)=>{
                if (ref.current) {
                    ref.current.refresh();
                }
            });
        });
    }, [
        isMobile,
        slideCount,
        mobileSplideOptions,
        desktopMultiSplideOptions
    ]);
    if (isEditing && hasNoLocations) {
        return /*#__PURE__*/ _jsx("section", {
            className: "overflow-visible",
            ...editingHydrationProps(isEditing),
            children: /*#__PURE__*/ _jsx(EditingHelpText, {
                priority: "warning",
                children: getDictionaryValue('OfficeLocationsHelpText') || 'Please select at least one office location in the Content Editor (page item fields) to display office information.'
            })
        });
    }
    if (hasNoLocations) {
        return /*#__PURE__*/ _jsx(_Fragment, {});
    }
    const { container, slider, slide, card, map, content, locationNameText, address, addressWrapper, phone, phoneRow, phoneLabel, openingHours, divider, button, pagination, arrowPrev, arrowNext, arrowIcon, openingHoursText, pairGrid, pairColumn, desktopTrack, openingHoursWrapper } = styles({
        layout
    });
    const splideClassName = isSingleCard ? undefined : 'w-screen mr-[calc(50%-50vw)]';
    const desktopSplideClassName = 'hidden lg:block w-full overflow-visible relative';
    const renderOfficeCard = (item, index)=>{
        const telephoneValue = item.telephone?.value;
        const imageField = item.image;
        const ctaText = [
            viewText
        ].filter(Boolean).join(' ');
        const slideKey = item.id ?? `office-location-${index}`;
        return /*#__PURE__*/ _jsxs("article", {
            className: card({
                layout
            }),
            children: [
                /*#__PURE__*/ _jsx("div", {
                    className: map({
                        layout
                    }),
                    children: imageField?.value?.src ? /*#__PURE__*/ _jsx(ImageWrapper, {
                        field: imageField,
                        className: "w-full h-full object-cover"
                    }) : /*#__PURE__*/ _jsx(ImageWrapper, {
                        field: {
                            value: {
                                src: '/assets/profile_placeholder.png',
                                alt: 'No image'
                            }
                        },
                        className: "w-full h-full object-cover"
                    })
                }),
                /*#__PURE__*/ _jsxs("div", {
                    className: content({
                        layout
                    }),
                    children: [
                        /*#__PURE__*/ _jsx(PlainTextWrapper, {
                            field: item.displayName,
                            tag: "h2",
                            className: locationNameText()
                        }),
                        /*#__PURE__*/ _jsx("div", {
                            ref: (el)=>setAddressRef(slideKey, el),
                            className: addressWrapper(),
                            children: /*#__PURE__*/ _jsx(PlainTextWrapper, {
                                field: {
                                    value: item.address
                                },
                                tag: "div",
                                className: address()
                            })
                        }),
                        telephoneValue && /*#__PURE__*/ _jsxs("div", {
                            className: phoneRow(),
                            children: [
                                /*#__PURE__*/ _jsx("a", {
                                    href: `tel:${telephoneValue}`,
                                    className: phone(),
                                    children: /*#__PURE__*/ _jsx(PlainTextWrapper, {
                                        field: item.telephone,
                                        tag: "span"
                                    })
                                }),
                                /*#__PURE__*/ _jsxs("span", {
                                    className: phoneLabel(),
                                    children: [
                                        "(",
                                        telephoneLabel || 'Telephone',
                                        ")"
                                    ]
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsxs("div", {
                            className: openingHoursWrapper(),
                            children: [
                                /*#__PURE__*/ _jsx("div", {
                                    className: openingHours(),
                                    children: openingHoursLabel
                                }),
                                /*#__PURE__*/ _jsx(RichTextWrapper, {
                                    field: item.openingHours,
                                    className: openingHoursText(),
                                    tag: "div"
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsx("div", {
                            className: divider()
                        }),
                        /*#__PURE__*/ _jsx(LinkWrapper, {
                            ctaComponentClass: "component-Location-block-button-2-color",
                            className: button(),
                            ctaVariant: "primary",
                            remixIcon: "arrow-right-line",
                            remixIconSize: "md",
                            field: {
                                value: {
                                    href: item.ctaHref || '#',
                                    text: ctaText,
                                    linktype: 'external'
                                }
                            },
                            suppressNewTabIcon: true
                        })
                    ]
                })
            ]
        });
    };
    const renderSplideSlides = ()=>validLocations.map((item, index)=>{
            const cardContent = renderOfficeCard(item, index);
            if (!cardContent) return null;
            const slideKey = item.id ?? `office-location-${index}`;
            return /*#__PURE__*/ _jsx(SplideSlide, {
                className: slide({
                    layout
                }),
                children: cardContent
            }, slideKey);
        });
    const renderMobileSlider = ()=>/*#__PURE__*/ _jsxs(Splide, {
            className: splideClassName,
            ref: (ref)=>{
                if (ref?.splide) {
                    mobileSplideRef.current = ref.splide;
                    requestAnimationFrame(equalizeAddressHeights);
                }
            },
            hasTrack: false,
            options: mobileSplideOptions,
            children: [
                /*#__PURE__*/ _jsx(SplideTrack, {
                    className: slider({
                        layout
                    }),
                    children: renderSplideSlides()
                }),
                slideCount > 1 && /*#__PURE__*/ _jsx("ul", {
                    className: `splide__pagination ${pagination({
                        layout
                    })}`
                })
            ]
        });
    return /*#__PURE__*/ _jsx("section", {
        className: "overflow-visible -mx-[8px]",
        ...editingHydrationProps(isEditing),
        children: /*#__PURE__*/ _jsx("div", {
            className: container(),
            children: isTwoCards || isThreeCards ? /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    /*#__PURE__*/ _jsx("div", {
                        ref: pairGridRef,
                        className: pairGrid(),
                        children: validLocations.map((item, index)=>{
                            const cardContent = renderOfficeCard(item, index);
                            if (!cardContent) return null;
                            const slideKey = item.id ?? `office-location-${index}`;
                            return /*#__PURE__*/ _jsx("div", {
                                className: pairColumn(),
                                children: cardContent
                            }, slideKey);
                        })
                    }),
                    /*#__PURE__*/ _jsx("div", {
                        className: "lg:hidden",
                        children: renderMobileSlider()
                    })
                ]
            }) : layout === 'multi' ? /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        className: desktopSplideClassName,
                        children: [
                            showDesktopArrows && /*#__PURE__*/ _jsx(ButtonWrapper, {
                                ctaComponentClass: "default",
                                ctaVariant: "tertiary",
                                onClick: ()=>desktopSplideRef.current?.go('<'),
                                className: arrowPrev({
                                    isArrowDisabled: isPrevDisabled
                                }),
                                disabled: isPrevDisabled,
                                "aria-label": "Previous slide",
                                children: /*#__PURE__*/ _jsx(SvgIcon, {
                                    icon: "prev-arrow",
                                    className: arrowIcon(),
                                    viewBox: "0 0 18 21"
                                })
                            }),
                            /*#__PURE__*/ _jsxs(Splide, {
                                className: "w-full overflow-visible",
                                ref: (ref)=>{
                                    if (ref?.splide) {
                                        desktopSplideRef.current = ref.splide;
                                        requestAnimationFrame(equalizeAddressHeights);
                                    }
                                },
                                hasTrack: false,
                                onMounted: onDesktopSplideMove,
                                onMoved: onDesktopSplideMove,
                                options: desktopMultiSplideOptions,
                                children: [
                                    /*#__PURE__*/ _jsx(SplideTrack, {
                                        className: desktopTrack(),
                                        children: renderSplideSlides()
                                    }),
                                    slideCount > 3 && /*#__PURE__*/ _jsx("ul", {
                                        className: `splide__pagination ${pagination({
                                            layout
                                        })}`
                                    })
                                ]
                            }),
                            showDesktopArrows && /*#__PURE__*/ _jsx(ButtonWrapper, {
                                ctaComponentClass: "default",
                                ctaVariant: "tertiary",
                                onClick: ()=>desktopSplideRef.current?.go('>'),
                                className: arrowNext({
                                    isArrowDisabled: isNextDisabled
                                }),
                                disabled: isNextDisabled,
                                "aria-label": "Next slide",
                                children: /*#__PURE__*/ _jsx(SvgIcon, {
                                    icon: "prev-arrow",
                                    className: arrowIcon(),
                                    viewBox: "0 0 18 21"
                                })
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsx("div", {
                        className: "lg:hidden",
                        children: renderMobileSlider()
                    })
                ]
            }) : /*#__PURE__*/ _jsx(Splide, {
                ref: (ref)=>{
                    if (ref?.splide) {
                        splideRef.current = ref.splide;
                        requestAnimationFrame(equalizeAddressHeights);
                    }
                },
                hasTrack: false,
                options: singleSplideOptions,
                children: /*#__PURE__*/ _jsx(SplideTrack, {
                    className: slider({
                        layout
                    }),
                    children: renderSplideSlides()
                })
            }, `office-locations-${slideCount}`)
        })
    });
};
export default withStandardComponentWrapper(OfficeLocations, false);
const styles = tv({
    slots: {
        container: [
            'w-full',
            'overflow-visible'
        ],
        slider: [
            'w-full',
            '[&_.splide__list]:items-stretch',
            '!overflow-visible'
        ],
        desktopTrack: [
            'w-full',
            'lg:w-screen',
            'lg:mr-[calc(50%-50vw)]',
            '[&_.splide__track]:overflow-visible',
            '[&_.splide__list]:items-stretch',
            '!overflow-visible'
        ],
        slide: [
            'flex',
            '!h-auto',
            'p-[8px]'
        ],
        card: [
            'flex',
            'flex-col',
            'h-full',
            'w-full',
            'overflow-hidden',
            'rounded-general-border-radius-card',
            'bg-component-card-item-surface',
            'shadow-[0px_0px_8px_0px_rgba(0,0,0,0.1)]'
        ],
        map: [
            'overflow-hidden',
            'min-h-max',
            'aspect-[135/76]'
        ],
        content: [
            'flex',
            'flex-col',
            'flex-1',
            'gap-spacing-spacing-16',
            'text-left'
        ],
        locationNameText: [
            'text-typography-font-size-medium',
            'leading-typography-line-height-xlarge',
            'font-semibold',
            'color-component-card-item-title'
        ],
        address: [
            'text-typography-font-size-body-medium',
            'leading-[22px]',
            'color-component-card-item-body'
        ],
        addressWrapper: [
            'w-full'
        ],
        phone: [
            'underline',
            'text-typography-font-size-body-medium',
            'leading-[22px]',
            'color-component-card-item-body',
            'decoration-solid'
        ],
        phoneRow: [
            'flex',
            'flex-row',
            'gap-spacing-spacing-4',
            'items-baseline'
        ],
        phoneLabel: [
            'text-typography-font-size-body-medium',
            'leading-[22px]',
            'color-component-card-item-title'
        ],
        openingHoursWrapper: [
            'flex',
            'flex-col',
            'gap-spacing-spacing-16'
        ],
        openingHours: [
            'text-typography-font-size-body-medium',
            'leading-[22px]',
            'color-component-card-item-body',
            'font-normal'
        ],
        divider: [
            'w-component-section-divider-width',
            'h-component-section-divider-height',
            'bg-component-section-divider'
        ],
        button: [
            '!size-fit',
            '!text-left',
            'mt-auto'
        ],
        pagination: [
            'p-0',
            'pt-component-section-container-gutter',
            'flex',
            'justify-center',
            '!relative',
            '[&>li]:p-[6px]',
            '[&>li>button]:w-spacing-spacing-12',
            '[&>li>button]:h-spacing-spacing-12',
            '[&>li>button]:bg-transparent',
            '[&>li>button]:!border',
            '[&>li>button]:!border-color-core-color-1-Navy',
            '[&>li>button]:!border-solid',
            '[&>li>button]:rounded-full',
            '[&>li>button]:m-0',
            '[&>li>button.is-active]:bg-color-core-color-1-Navy',
            '[&>li>button.is-active]:transform-none',
            '[&>li>button.is-active]:opacity-100',
            '[&>li>button.is-active]:w-spacing-spacing-40',
            '!mt-[24px]'
        ],
        arrowPrev: [
            'absolute',
            'top-1/2',
            '-translate-y-1/2',
            'z-10',
            'border-general-border-width-pagination-button',
            'border-solid',
            'border-component-carousel-utility-bar-button-border',
            'rounded-general-border-radius-tag',
            'text-white',
            '!p-spacing-spacing-12',
            'hover:opacity-90',
            'transition-opacity',
            'hidden',
            'lg:block',
            'lg:left-[-2vw]',
            'xl:!left-[-4.5vw]',
            'bg-component-carousel-utility-bar-button-bg'
        ],
        arrowNext: [
            'absolute',
            'top-1/2',
            '-translate-y-1/2',
            'lg:right-[-2vw]',
            'right-0',
            'z-10',
            'border-general-border-width-pagination-button',
            'border-solid',
            'border-component-carousel-utility-bar-button-border',
            'rounded-general-border-radius-tag',
            'text-white',
            '!p-spacing-spacing-12',
            'rotate-180',
            'hover:opacity-90',
            'transition-opacity',
            'hidden',
            'lg:block',
            'bg-component-carousel-utility-bar-button-bg'
        ],
        arrowIcon: [
            '!h-[18px]',
            '!w-[18px]'
        ],
        openingHoursText: [
            'gap-spacing-spacing-4',
            '[&>*]:!text-typography-font-size-body-medium',
            '[&>*]:!leading-[22px]',
            '[&>*]:!color-component-card-item-body',
            '[&>p]:!mb-[0px]'
        ],
        pairGrid: [
            'hidden',
            'lg:flex',
            'lg:w-full',
            'lg:gap-2',
            'lg:items-stretch',
            'lg:justify-center'
        ],
        pairColumn: [
            'p-[8px]',
            'flex'
        ]
    },
    variants: {
        layout: {
            single: {
                slider: [
                    '[&_.splide__slide]:!w-full',
                    '[&_.splide__track]:overflow-visible'
                ],
                slide: [
                    '!w-full'
                ],
                card: [
                    'lg:flex-row',
                    'h-auto'
                ],
                map: [
                    'w-full',
                    // 'h-[260px]',
                    'lg:w-1/2',
                    'lg:flex-1',
                    'lg:h-full',
                    'lg:min-h-full',
                    'lg:rounded-l-general-border-radius-card',
                    'lg:rounded-tr-none',
                    'lg:rounded-br-none'
                ],
                content: [
                    'w-full',
                    'p-component-card-item-portrait-content-padding-top',
                    'lg:w-1/2',
                    'lg:flex-1',
                    'lg:px-spacing-spacing-40',
                    'lg:py-spacing-spacing-40',
                    'items-start'
                ]
            },
            pair: {
                slider: [
                    'w-full',
                    '[&_.splide__track]:overflow-hidden'
                ],
                card: [
                    'h-auto',
                    'lg:h-full'
                ],
                map: [
                    'w-full'
                ],
                content: [
                    'w-full',
                    'p-component-card-item-portrait-content-padding-top',
                    'lg:px-spacing-spacing-40',
                    'lg:py-spacing-spacing-40',
                    'lg:items-start'
                ],
                pairColumn: [
                    'lg:w-1/3'
                ]
            },
            multi: {
                slider: [
                    'w-full',
                    '![&_.splide__track]:overflow-visible'
                ],
                // map: ['h-[260px]'],
                content: [
                    'p-component-card-item-portrait-content-padding-top'
                ],
                pairColumn: [
                    'lg:w-1/3'
                ]
            }
        },
        isArrowDisabled: {
            true: {
                arrowPrev: [
                    'opacity-50',
                    'cursor-not-allowed'
                ],
                arrowNext: [
                    'opacity-50',
                    'cursor-not-allowed'
                ]
            }
        }
    },
    defaultVariants: {
        layout: 'multi',
        isArrowDisabled: false
    }
});
